import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api';

export interface ReminderConfig {
  id: string;
  tenantId: string;
  name?: string;
  trigger: 'BEFORE_DUE' | 'AFTER_DUE' | 'OVERDUE_15' | 'OVERDUE_30' | 'OVERDUE_60' | 'SUSPENSION_WARNING';
  daysOffset: number;
  messageTemplate: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentReminderHistory {
  id: string;
  tenantId: string;
  accountId: string;
  installmentId?: string;
  trigger: string;
  status: 'REMINDER_PENDING' | 'REMINDER_SENT' | 'REMINDER_FAILED';
  scheduledFor: Date;
  sentAt?: Date;
  message?: string;
  student: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  account: {
    balance: number;
  };
}

export interface ReminderHistoryResponse {
  data: PaymentReminderHistory[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ReminderConfigsResponse {
  data: ReminderConfig[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Build query string from parameters
 */
function buildQueryString(params?: Record<string, any>): string {
  if (!params) return '';
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Get all reminder configurations with optional filters
 */
export const getReminderConfigs = async (query?: {
  trigger?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}): Promise<ReminderConfigsResponse> => {
  const queryString = buildQueryString({ limit: 100, ...query });
  return apiGet<ReminderConfigsResponse>(`/financial/reminders/configs${queryString}`);
};

/**
 * Get a specific reminder configuration by ID
 */
export const getReminderConfig = async (id: string): Promise<ReminderConfig> => {
  return apiGet<ReminderConfig>(`/financial/reminders/configs/${id}`);
};

/**
 * Create a new reminder configuration
 */
export const createReminderConfig = async (
  data: Omit<ReminderConfig, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>
): Promise<ReminderConfig> => {
  return apiPost<ReminderConfig>('/financial/reminders/configs', data);
};

/**
 * Update an existing reminder configuration
 */
export const updateReminderConfig = async (
  id: string,
  data: Partial<Omit<ReminderConfig, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>>
): Promise<ReminderConfig> => {
  return apiPut<ReminderConfig>(`/financial/reminders/configs/${id}`, data);
};

/**
 * Delete (deactivate) a reminder configuration
 */
export const deleteReminderConfig = async (id: string): Promise<void> => {
  return apiDelete<void>(`/financial/reminders/configs/${id}`);
};

/**
 * Get reminder history with filters
 */
export const getReminderHistory = async (query?: {
  studentId?: string;
  status?: string;
  trigger?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<ReminderHistoryResponse> => {
  const queryString = buildQueryString({ limit: 100, ...query });
  return apiGet<ReminderHistoryResponse>(`/financial/reminders/history${queryString}`);
};

/**
 * Manually send a reminder to a specific student account
 */
export const sendManualReminder = async (data: {
  accountId: string;
  installmentId?: string;
  configId?: string;
  message?: string;
  trigger?: string;
}): Promise<PaymentReminderHistory> => {
  return apiPost<PaymentReminderHistory>('/financial/reminders/send', data);
};

/**
 * Trigger manual reminder processing (for testing/admin purposes)
 */
export const processReminders = async (): Promise<{
  processed: number;
  reminders: PaymentReminderHistory[];
}> => {
  return apiPost<{ processed: number; reminders: PaymentReminderHistory[] }>('/financial/reminders/process');
};
